import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Plus, ExternalLink, Copy, Trash2, Loader2,
  Edit3, X, CheckCircle2, XCircle, Clock, UserX,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_OPTIONS = [
  { value: "proposed", label: "Vorgeschlagen" },
  { value: "booked", label: "Gebucht" },
  { value: "completed", label: "Durchgeführt" },
  { value: "cancelled", label: "Abgesagt" },
  { value: "no_show", label: "No-Show" },
];

const STATUS_TONE = {
  proposed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  booked: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  completed: "border-zinc-500/40 bg-zinc-700/20 text-zinc-300",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-300",
  no_show: "border-amber-500/40 bg-amber-500/10 text-amber-300",
};

const STATUS_ICON = {
  proposed: Clock,
  booked: CalendarDays,
  completed: CheckCircle2,
  cancelled: XCircle,
  no_show: UserX,
};

function fmt(iso, opts) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", opts || {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fromLocalInput(value) {
  if (!value) return null;
  try { return new Date(value).toISOString(); } catch { return value; }
}

function StatusBadge({ value }) {
  const Icon = STATUS_ICON[value] || CalendarDays;
  const opt = STATUS_OPTIONS.find((o) => o.value === value);
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${STATUS_TONE[value] || ""}`}>
      <Icon className="h-3 w-3" />
      {opt?.label || value}
    </span>
  );
}

export default function AppointmentsTab({ leadId, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${leadId}/events`, {
        headers: authHeaders(), timeout: 15000,
      });
      setItems(r.data?.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/crm/events/${id}`, { headers: authHeaders(), timeout: 15000 });
      toast.success("Termin gelöscht");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/crm/events/${id}`,
        { meeting_status: newStatus },
        { headers: authHeaders(), timeout: 15000 },
      );
      toast.success("Status aktualisiert");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <section data-testid="journey-appointments-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-100">Termine</h2>
        <button
          type="button"
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="journey-appointments-new"
          className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Neuer Termin
        </button>
      </div>

      {error ? (
        <div className="rounded border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200">
          {String(error)}
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded bg-white/[0.03]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/[0.06] p-6 text-center text-sm text-zinc-400">
          <CalendarDays className="mx-auto mb-2 h-5 w-5 text-zinc-600" />
          Noch keine Termine für diesen Lead.
        </div>
      ) : (
        <ul data-testid="journey-appointments-list" className="space-y-2">
          {items.map((ev) => (
            <li
              key={ev.id}
              data-testid={`journey-appointment-${ev.id}`}
              className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4 text-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-zinc-100">{ev.title}</span>
                    <StatusBadge value={ev.meeting_status} />
                  </div>
                  <div className="mt-1 text-xs text-zinc-400">
                    {fmt(ev.starts_at)}
                    {ev.ends_at ? <> – {fmt(ev.ends_at, { hour: "2-digit", minute: "2-digit" })}</> : null}
                    {ev.location ? <> · {ev.location}</> : null}
                  </div>
                  {ev.description ? <p className="mt-2 whitespace-pre-wrap text-xs text-zinc-300">{ev.description}</p> : null}
                  {ev.note ? <p className="mt-1 text-xs text-zinc-500">Notiz: {ev.note}</p> : null}
                  {ev.attendees?.length ? (
                    <p className="mt-1 text-[11px] text-zinc-500">Teilnehmer: {ev.attendees.join(", ")}</p>
                  ) : null}
                  {ev.meeting_link ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <a
                        href={ev.meeting_link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`journey-appointment-open-${ev.id}`}
                        className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-500/20"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Meeting öffnen
                      </a>
                      <button
                        type="button"
                        onClick={() => copyLink(ev.meeting_link)}
                        data-testid={`journey-appointment-copy-${ev.id}`}
                        className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-[11px] text-zinc-300 hover:border-white/20 hover:bg-white/5"
                      >
                        <Copy className="h-3 w-3" />
                        Link kopieren
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <select
                    value={ev.meeting_status}
                    onChange={(e) => handleStatusChange(ev.id, e.target.value)}
                    data-testid={`journey-appointment-status-${ev.id}`}
                    className="rounded border border-white/10 bg-[#0B0F10] px-1.5 py-1 text-[10px] text-zinc-200"
                  >
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setEditing(ev); setShowForm(true); }}
                    data-testid={`journey-appointment-edit-${ev.id}`}
                    className="rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
                    title="Bearbeiten"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(ev.id)}
                    data-testid={`journey-appointment-delete-${ev.id}`}
                    className="rounded p-1 text-zinc-400 hover:bg-red-500/10 hover:text-red-300"
                    title="Löschen"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <AppointmentForm
          leadId={leadId}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { await load(); onChange?.(); }}
        />
      ) : null}
    </section>
  );
}

function AppointmentForm({ leadId, editing, onClose, onSaved }) {
  const [title, setTitle] = useState(editing?.title || "");
  const [startsAt, setStartsAt] = useState(toLocalInput(editing?.starts_at) || "");
  const [endsAt, setEndsAt] = useState(toLocalInput(editing?.ends_at) || "");
  const [meetingStatus, setMeetingStatus] = useState(editing?.meeting_status || "booked");
  const [meetingLink, setMeetingLink] = useState(editing?.meeting_link || "");
  const [location, setLocation] = useState(editing?.location || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [note, setNote] = useState(editing?.note || "");
  const [attendees, setAttendees] = useState((editing?.attendees || []).join(", "));
  const [createTask, setCreateTask] = useState(false);
  const [taskDueAt, setTaskDueAt] = useState("");
  const [saving, setSaving] = useState(false);

  const isEdit = !!editing;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !startsAt) {
      toast.error("Titel und Startzeit sind erforderlich");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        starts_at: fromLocalInput(startsAt),
        ends_at: endsAt ? fromLocalInput(endsAt) : null,
        meeting_status: meetingStatus,
        meeting_link: meetingLink || null,
        location: location || null,
        description: description || null,
        note: note || null,
        attendees: attendees ? attendees.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };

      if (isEdit) {
        await axios.patch(
          `${API}/admin/crm/events/${editing.id}`,
          body,
          { headers: authHeaders(), timeout: 15000 },
        );
        toast.success("Termin aktualisiert");
      } else {
        await axios.post(
          `${API}/admin/crm/leads/${leadId}/events`,
          {
            ...body,
            create_task: createTask,
            task_due_at: createTask && taskDueAt ? fromLocalInput(taskDueAt) : null,
          },
          { headers: authHeaders(), timeout: 15000 },
        );
        toast.success(createTask ? "Termin + Vorbereitungs-Aufgabe erstellt" : "Termin erstellt");
      }
      onClose();
      await onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail || err?.message;
      toast.error(msg || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="journey-appointment-form"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto rounded-md border border-white/[0.08] bg-[#0B0F10] p-5"
      >
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">
            {isEdit ? "Termin bearbeiten" : "Neuer Termin"}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-zinc-500 hover:bg-white/5">
            <X className="h-4 w-4" />
          </button>
        </header>

        <Field label="Titel *">
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="journey-appointment-field-title"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Start *">
            <input
              type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
              data-testid="journey-appointment-field-starts-at"
              className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
            />
          </Field>
          <Field label="Ende (optional)">
            <input
              type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
              data-testid="journey-appointment-field-ends-at"
              className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
            />
          </Field>
        </div>

        <Field label="Status">
          <select
            value={meetingStatus} onChange={(e) => setMeetingStatus(e.target.value)}
            data-testid="journey-appointment-field-status"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>

        <Field label="Meeting-Link">
          <input
            type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}
            data-testid="journey-appointment-field-link"
            placeholder="https://meet.google.com/..."
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        <Field label="Ort">
          <input
            type="text" value={location} onChange={(e) => setLocation(e.target.value)}
            data-testid="journey-appointment-field-location"
            placeholder="z.B. Online · Berlin"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        <Field label="Beschreibung">
          <textarea
            rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
            data-testid="journey-appointment-field-description"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        <Field label="Notiz">
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            data-testid="journey-appointment-field-note"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        <Field label="Teilnehmer (kommagetrennt)">
          <input
            type="text" value={attendees} onChange={(e) => setAttendees(e.target.value)}
            data-testid="journey-appointment-field-attendees"
            placeholder="anna@example.com, bo@example.com"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </Field>

        {!isEdit ? (
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input
              type="checkbox" checked={createTask} onChange={(e) => setCreateTask(e.target.checked)}
              data-testid="journey-appointment-field-create-task"
              className="h-3.5 w-3.5 rounded border border-white/20 bg-[#050607]"
            />
            Aufgabe zur Terminvorbereitung erstellen (24 h vorher)
          </label>
        ) : null}

        {createTask && !isEdit ? (
          <Field label="Aufgaben-Fälligkeit (optional)">
            <input
              type="datetime-local" value={taskDueAt} onChange={(e) => setTaskDueAt(e.target.value)}
              data-testid="journey-appointment-field-task-due"
              className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
            />
          </Field>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button" onClick={onClose}
            className="rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-white/20 hover:bg-white/5"
          >
            Abbrechen
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="journey-appointment-save"
            className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {isEdit ? "Aktualisieren" : "Termin speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
